import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface TransactionSubType {
  name: string;
  value: string;
}

export interface TransactionType {
  name: string;
  value: string;
  subTypes: TransactionSubType[];
}

export interface Transaction {
  id: number;
  job_id: number;
  job_ref_parent?: number;
  transaction_type: 'charge' | 'payment' | 'adjustment';
  transaction_sub_type: string;
  amount: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface TransactionDetail {
  id: number;
  job_id: number;
  job_ref_parent?: number;
  job_ref_child?: number[];
  transaction_type: 'charge' | 'payment' | 'adjustment';
  transaction_sub_type: string;
  amount: number;
  description?: string;
  related_transactions?: Transaction[];
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  private apiUrl = '/api/transactions';

  // Transaction type definitions
  private readonly transactionTypes: TransactionType[] = [
    {
      name: 'Charge',
      value: 'charge',
      subTypes: [
        { name: 'Inspection', value: 'Inspection' },
        { name: 'Credit Card Fee - Inspection', value: 'Credit Card Fee - Inspection' },
        { name: 'Completion', value: 'Completion' },
        { name: 'Interest', value: 'Interest' },
        { name: 'Completion - Inspector Miss', value: 'Completion - Inspector Miss' },
        { name: 'Completion - Crew Miss', value: 'Completion - Crew Miss' },
        { name: 'Credit Card Fee - Completion', value: 'Credit Card Processing Fee' },
        { name: 'Additional Work', value: 'Additional Work' },
        { name: 'Bait Service', value: 'Bait Service' },
        { name: 'Warranty', value: 'Warranty' }
      ]
    },
    {
      name: 'Payment',
      value: 'payment',
      subTypes: [
        { name: 'Inspection', value: 'Inspection' },
        { name: 'Credit Card Fee - Inspection', value: 'Credit Card Fee - Inspection' },
        { name: 'Completion', value: 'Completion' },
        { name: 'Interest', value: 'Interest' },
        { name: 'Completion - Inspector Miss', value: 'Completion - Inspector Miss' },
        { name: 'Completion - Crew Miss', value: 'Completion - Crew Miss' },
        { name: 'Credit Card Fee - Completion', value: 'Credit Card Processing Fee' },
        { name: 'Additional Work', value: 'Additional Work' },
        { name: 'Bait Service', value: 'Bait Service' },
        { name: 'Warranty', value: 'Warranty' }
      ]
    },
    {
      name: 'Adjustment',
      value: 'adjustment',
      subTypes: [
        { name: 'Inspection Discount - Prepay', value: 'Inspection Discount - Prepay' },
        { name: 'Inspection Discount - Package', value: 'Inspection Discount - Package' },
        { name: 'Inspection Discount - Inspector', value: 'Inspection Discount - Inspector' },
        { name: 'Inspection Discount - Customer Satisfaction', value: 'Inspection Discount - Customer Satisfaction' },
        { name: 'Completion Discount - Inspector', value: 'Completion Discount - Inspector' },
        { name: 'Completion Discount - Crew Manager', value: 'Completion Discount - Crew Manager' },
        { name: 'Completion Discount - Executive', value: 'Completion Discount - Executive' },
        { name: 'Completion Discount - Customer Satisfaction', value: 'Completion Discount - Customer Satisfaction' },
        { name: 'Completion - Inspector Miss', value: 'Completion - Inspector Miss' },
        { name: 'Completion - Crew Miss', value: 'Completion - Crew Miss' },
        { name: 'Inspection Adjustment - Accounting', value: 'Inspection Adjustment - Accounting' },
        { name: 'Completion Adjustment - Accounting', value: 'Adjustment - Accounting' },
        { name: 'Inspection Discount - Marketing', value: 'Inspection Discount - Marketing' }
      ]
    }
  ];

  constructor(private http: HttpClient) { }

  /**
   * Get all transaction types with their sub types
   */
  getTransactionTypes(): TransactionType[] {
    return this.transactionTypes;
  }

  /**
   * Get transaction type by value
   */
  getTransactionType(typeValue: string): TransactionType | undefined {
    return this.transactionTypes.find(t => t.value === typeValue);
  }

  /**
   * Get sub types for a specific transaction type
   */
  getSubTypesForType(typeValue: string): TransactionSubType[] {
    const type = this.getTransactionType(typeValue);
    return type ? type.subTypes : [];
  }

  /**
   * Get sub type name by value
   */
  getSubTypeName(typeValue: string, subTypeValue: string): string {
    const subTypes = this.getSubTypesForType(typeValue);
    const subType = subTypes.find(s => s.value === subTypeValue);
    return subType ? subType.name : subTypeValue;
  }

  /**
   * Get transactions for a job (including parent and child job references)
   */
  getJobTransactions(jobId: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/job/${jobId}`);
  }

  /**
   * Get detailed transaction information
   */
  getTransactionDetail(transactionId: number): Observable<TransactionDetail> {
    return this.http.get<TransactionDetail>(`${this.apiUrl}/${transactionId}`);
  }

  /**
   * Get all related transactions for a job (parent and all children)
   */
  getRelatedJobTransactions(jobId: number, jobRefParent?: number): Observable<any> {
    let url = `${this.apiUrl}/job-family/${jobId}`;
    if (jobRefParent) {
      url += `?parent_ref=${jobRefParent}`;
    }
    return this.http.get<any>(url);
  }

  /**
   * Format transaction amount as currency
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  /**
   * Get transaction type display name
   */
  getTransactionTypeDisplayName(typeValue: string): string {
    const type = this.getTransactionType(typeValue);
    return type ? type.name : typeValue;
  }

  /**
   * Check if transaction is a charge (user needs to pay)
   */
  isChargeTransaction(transaction: Transaction | TransactionDetail): boolean {
    return transaction.transaction_type === 'charge' && transaction.amount > 0;
  }

  /**
   * Check if transaction is a payment (user paid)
   */
  isPaymentTransaction(transaction: Transaction | TransactionDetail): boolean {
    return transaction.transaction_type === 'payment' && transaction.amount > 0;
  }

  /**
   * Check if transaction is a discount/adjustment
   */
  isAdjustmentTransaction(transaction: Transaction | TransactionDetail): boolean {
    return transaction.transaction_type === 'adjustment';
  }

  /**
   * Get transaction category for grouping
   */
  getTransactionCategory(transaction: Transaction | TransactionDetail): string {
    const subTypeName = this.getSubTypeName(transaction.transaction_type, transaction.transaction_sub_type);

    if (subTypeName.includes('Inspection')) {
      return 'Inspection';
    } else if (subTypeName.includes('Completion')) {
      return 'Completion';
    } else if (subTypeName.includes('Credit Card')) {
      return 'Credit Card Fee';
    } else if (subTypeName.includes('Discount')) {
      return 'Discount';
    } else if (subTypeName.includes('Adjustment')) {
      return 'Adjustment';
    } else {
      return 'Other';
    }
  }
}
